/**
 * Provider stack for the admin SPA.
 *
 * QueryClient → i18n → WebSocket → Router. The websocket sits inside QueryClientProvider
 * because its whole job is to invalidate query keys (see lib/live.ts): it is a cache
 * invalidation signal, not a second source of truth.
 *
 * The 401 handler is installed ONCE, from @most/auth. Three SPAs with three copies of that
 * logic would be three different bugs (plans.md §7.5).
 */
import { useEffect, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { installAuthInterceptor } from '@most/auth';
import { I18nProvider } from '@most/i18n';
import { WsProvider } from '@most/ws-client';
import { Toaster, TooltipProvider } from '@most/ui';
import { i18n } from '@/i18n';
import { useLiveChannelEnabled } from '@/lib/live';
import { AppRouter } from './router';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The admin is a work interface on a desk: refetching on every window focus would make
      // tables jump under the cursor mid-read. Freshness comes from the websocket instead —
      // WHEN THERE IS ONE. `LiveGate` below turns this back on if the server has no live
      // channel, because "don't refetch, the socket will tell us" plus "there is no socket"
      // is how a dashboard quietly shows an hours-old order count.
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Global 401 → clear the cache and bounce to /login?next=… . One copy, in the shared package.
// The redirect is a full-page navigation: /login belongs to the Next.js landing, another build.
installAuthInterceptor(queryClient, (to) => {
  window.location.assign(to);
});

/**
 * Connects the socket only if the server actually serves it, and makes sure the admin is never
 * left with NEITHER freshness mechanism: no socket → refetch on window focus instead.
 *
 * Must sit inside QueryClientProvider — it reads `/config` through the query cache.
 */
function LiveGate({ children }: { children: ReactNode }) {
  const liveEnabled = useLiveChannelEnabled();

  useEffect(() => {
    queryClient.setDefaultOptions({
      queries: {
        refetchOnWindowFocus: !liveEnabled,
        retry: 1,
        staleTime: 30_000,
      },
      mutations: { retry: 0 },
    });
  }, [liveEnabled]);

  return <WsProvider enabled={liveEnabled}>{children}</WsProvider>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider i18n={i18n}>
        <LiveGate>
          <TooltipProvider>
            {/*
             * Toaster IS the toast context provider (ToastProvider under a shadcn-shaped alias),
             * so it must WRAP the tree — every useToast() call reads this context. It renders its
             * own fixed overlay for the toasts internally; there is no separate childless mount.
             */}
            <Toaster>
              {/* No basename: since D16 this SPA owns adm.mostholding.org and is served
                  from the root of it, matching vite's `base: '/'`. */}
              <BrowserRouter>
                <AppRouter />
              </BrowserRouter>
            </Toaster>
          </TooltipProvider>
        </LiveGate>
      </I18nProvider>
    </QueryClientProvider>
  );
}
